import {
  type DimensionCatalogEntry,
  type MetricQuerySpec,
  type MetricRegistry,
  type MetricRegistryAppliesTo,
  type MetricRegistryEntry,
} from './metric-registry';
import { validateMetricQueryDimensions } from './metric-query-dimension-validation';

export const METRIC_QUERY_COMPILER_REFUSAL_CODES = {
  unknownMetric: 'unknown_metric',
  unknownDimensionMember: 'unknown_dimension_member',
  blendRefused: 'blend_refused',
  grainNotDeclared: 'grain_not_declared',
  grainBudgetExceeded: 'grain_budget_exceeded',
  invalidPeriodWindow: 'invalid_period_window',
  artifactFamilyRequired: 'artifact_family_required',
  snapshotScopeRequired: 'snapshot_scope_required',
  snapshotScopeInvalid: 'snapshot_scope_invalid',
} as const;

export type MetricQueryCompilerRefusalCode =
  (typeof METRIC_QUERY_COMPILER_REFUSAL_CODES)[keyof typeof METRIC_QUERY_COMPILER_REFUSAL_CODES];

export type MetricQueryCompilerRefusal = Readonly<{
  code: MetricQueryCompilerRefusalCode;
  message: string;
  details: Readonly<Record<string, unknown>>;
  counterProposal?: Partial<MetricQuerySpec>;
}>;

export type MetricQueryFactWhere = Readonly<Record<string, unknown>>;

export type MetricQueryResolutionProvenance = Readonly<{
  requestedMetricRef: string;
  canonicalMetricKey: string;
  resolutionSource: 'exact_key' | 'exact_alias' | 'governed_label';
}>;

export type MetricQueryCompiledPlan = Readonly<{
  tenantId: string;
  metricKeys: readonly string[];
  period: MetricQuerySpec['period'];
  groupBy: readonly string[];
  snapshot?: MetricQuerySpec['snapshot'];
  factWhere: MetricQueryFactWhere;
  dimensionFilters: readonly MetricQueryDimensionFilterPlan[];
  estimatedCells: number;
  // Variance / dimensional first consumer (issue #2111): requested alias -> canonical
  // metricId for every key the compiler resolved through the registry alias map.
  resolvedMetricAliases: Readonly<Record<string, string>>;
  metricResolutionProvenance: readonly MetricQueryResolutionProvenance[];
}>;

export type MetricQueryDimensionFilterPlan = Readonly<{
  dimension: string;
  physicalColumn: MetricFactDimensionColumn;
  memberKeys: readonly string[];
}>;

export type MetricQueryCompileAccepted = Readonly<{
  ok: true;
  plan: MetricQueryCompiledPlan;
}>;

export type MetricQueryCompileRefused = Readonly<{
  ok: false;
  refusal: MetricQueryCompilerRefusal;
}>;

export type MetricQueryCompileResult = MetricQueryCompileAccepted | MetricQueryCompileRefused;

export type MetricFactDimensionColumn =
  | 'businessLineKey'
  | 'geoKey'
  | 'customerKey'
  | 'laneKey'
  | 'siteKey'
  | 'glAccountKey'
  | 'functionKey'
  | 'subfunctionKey';

export type CompileMetricQuerySpecInput = Readonly<{
  tenantId: string;
  spec: MetricQuerySpec;
  registry: MetricRegistry;
  dimensionMembersByDimension: Readonly<Record<string, readonly string[]>>;
}>;

export function compileMetricQuerySpec(
  input: CompileMetricQuerySpecInput,
): MetricQueryCompileResult {
  const periodResult = validatePeriodWindow(input.spec.period);
  if (!periodResult.ok) return periodResult;

  const metricsResult = resolveMetrics(input);
  if (!metricsResult.ok) return metricsResult;

  const factScopeResult = validateFactScope(input.spec, metricsResult.metricEntries);
  if (!factScopeResult.ok) return factScopeResult;

  const dimensionResult = validateMetricQueryDimensions(input, refused);
  if (!dimensionResult.ok) return dimensionResult;

  const grainResult = validateDeclaredGrain(input, metricsResult.metricEntries);
  if (!grainResult.ok) return grainResult;

  const blendResult = validateBlend(input, metricsResult.metricEntries);
  if (!blendResult.ok) return blendResult;

  const budgetResult = validateBudget(input);
  if (!budgetResult.ok) return budgetResult;

  if (dimensionResult.artifactFamilyRequired) {
    return refused(
      'artifact_family_required',
      'Requested dimension requires its artifact family.',
      dimensionResult.artifactFamilyRequired,
    );
  }

  const factWhere = buildFactWhere(
    input,
    metricsResult.canonicalMetricKeys,
    dimensionResult.dimensionFilters,
    factScopeResult,
  );

  return {
    ok: true,
    plan: {
      tenantId: input.tenantId,
      metricKeys: metricsResult.canonicalMetricKeys,
      period: input.spec.period,
      groupBy: input.spec.groupBy,
      ...(factScopeResult.factScope === 'gl_dataset_run'
        ? { snapshot: factScopeResult.snapshot }
        : {}),
      factWhere,
      dimensionFilters: dimensionResult.dimensionFilters,
      estimatedCells: budgetResult.estimatedCells,
      resolvedMetricAliases: metricsResult.resolvedMetricAliases,
      metricResolutionProvenance: metricsResult.metricResolutionProvenance,
    },
  };
}

function validatePeriodWindow(period: MetricQuerySpec['period']): MetricQueryCompileResult {
  if (!period.type || !period.from || !period.to) {
    return refused('invalid_period_window', 'Metric query period must include type, from, and to.', {
      period,
    });
  }

  const from = parsePeriodKey(period.type, period.from);
  const to = parsePeriodKey(period.type, period.to);
  if (from === null || to === null || from > to) {
    return refused('invalid_period_window', 'Metric query period window is invalid.', { period });
  }

  return { ok: true, plan: emptyPlan };
}

type ResolvedMetrics = Readonly<{
  ok: true;
  metricEntries: readonly MetricRegistryEntry[];
  canonicalMetricKeys: readonly string[];
  resolvedMetricAliases: Readonly<Record<string, string>>;
  metricResolutionProvenance: readonly MetricQueryResolutionProvenance[];
}>;

function resolveMetrics(
  input: CompileMetricQuerySpecInput,
): ResolvedMetrics | MetricQueryCompileRefused {
  const metricEntries: MetricRegistryEntry[] = [];
  const canonicalMetricKeys: string[] = [];
  const resolvedMetricAliases: Record<string, string> = {};
  const metricResolutionProvenance: MetricQueryResolutionProvenance[] = [];
  for (const metricKey of input.spec.metrics) {
    const entry = input.registry.resolveMetric(metricKey);
    if (!entry) {
      return refused(
        'unknown_metric',
        `Metric ${metricKey} is not registered (no governed metric id or alias).`,
        { metricKey },
      );
    }
    if (entry.metricKey !== metricKey) resolvedMetricAliases[metricKey] = entry.metricKey;
    metricResolutionProvenance.push({
      requestedMetricRef: metricKey,
      canonicalMetricKey: entry.metricKey,
      resolutionSource: entry.metricKey === metricKey ? 'exact_key' : 'exact_alias',
    });
    metricEntries.push(entry);
    canonicalMetricKeys.push(entry.metricKey);
  }
  return {
    ok: true,
    metricEntries,
    canonicalMetricKeys,
    resolvedMetricAliases,
    metricResolutionProvenance,
  };
}

type FactScopeValidation =
  | Readonly<{ ok: true; factScope: 'legacy' }>
  | Readonly<{
      ok: true;
      factScope: 'gl_dataset_run';
      snapshot: NonNullable<MetricQuerySpec['snapshot']>;
    }>
  | MetricQueryCompileRefused;

function validateFactScope(
  spec: MetricQuerySpec,
  metricEntries: readonly MetricRegistryEntry[],
): FactScopeValidation {
  const scopes = new Set(metricEntries.map((entry) => entry.factScope ?? 'legacy'));
  if (scopes.size !== 1) {
    return refused(
      'snapshot_scope_invalid',
      'A metric query cannot mix legacy and dataset-run fact scopes.',
      { metricKeys: metricEntries.map((entry) => entry.metricKey).sort() },
    );
  }
  const factScope = [...scopes][0];
  if (factScope === 'gl_dataset_run') {
    if (spec.snapshot?.datasetId && spec.snapshot.runId) {
      return { ok: true, factScope, snapshot: spec.snapshot };
    }
    return refused(
      'snapshot_scope_required',
      'This metric requires a selected dataset and completed run.',
      { metricKeys: metricEntries.map((entry) => entry.metricKey).sort() },
    );
  }
  if (spec.snapshot) {
    return refused(
      'snapshot_scope_invalid',
      'A dataset-run snapshot can only be used with a dataset-run metric.',
      { metricKeys: metricEntries.map((entry) => entry.metricKey).sort() },
    );
  }
  return { ok: true, factScope: 'legacy' };
}

function validateDeclaredGrain(
  input: CompileMetricQuerySpecInput,
  metricEntries: readonly MetricRegistryEntry[],
): MetricQueryCompileResult {
  for (const entry of metricEntries) {
    for (const requestedGrain of input.spec.grain.level) {
      if (requestedGrain.dimension === 'period') continue;
      const declared = entry.aggregationRules.some(
        (rule) =>
          rule.dimension === requestedGrain.dimension && rule.rollup === requestedGrain.rollup,
      );
      if (!declared) {
        return refused(
          'grain_not_declared',
          'Requested metric grain is not declared by the governed registry.',
          { metricKey: entry.metricKey, requestedGrain },
        );
      }
    }
  }

  return { ok: true, plan: emptyPlan };
}

function validateBlend(
  input: CompileMetricQuerySpecInput,
  metricEntries: readonly MetricRegistryEntry[],
): MetricQueryCompileResult {
  const requestedDimension =
    input.spec.grain.level.find((level) => level.dimension !== 'period')?.dimension ??
    input.spec.groupBy.find((dimension) => dimension !== 'period') ??
    input.spec.filters.find((filter) => filter.dimension !== 'period')?.dimension ??
    'business_line';
  const requestedMembers = input.spec.filters
    .filter((filter) => filter.dimension === requestedDimension)
    .flatMap((filter) => [...filter.memberKeys]);

  for (const entry of metricEntries) {
    if (!entryAppliesToMembers(entry, requestedDimension, requestedMembers)) {
      return refused('unknown_dimension_member', 'Requested member is outside metric scope.', {
        dimension: requestedDimension,
        memberKeys: requestedMembers,
        metricKey: entry.metricKey,
        appliesTo: entry.appliesTo,
      });
    }
  }

  const frames = [...new Set(metricEntries.map((entry) => entry.frame))].sort();
  if (frames.length <= 1) return { ok: true, plan: emptyPlan };

  const hasNeverBlendMetric = metricEntries.some((entry) => entry.blendPolicy.kind === 'never_blend');
  const dimensionCatalog = input.registry.getDimensionCatalog(requestedDimension);
  const dimensionNeverBlendApplies = dimensionCatalogNeverBlendApplies(
    dimensionCatalog,
    requestedDimension,
    requestedMembers,
  );

  if (hasNeverBlendMetric || dimensionNeverBlendApplies) {
    return refused('blend_refused', 'Registry never-blend rule blocks this metric combination.', {
      registryRule: 'never_blend',
      dimension: requestedDimension,
      memberKeys: requestedMembers,
      frames,
      metricKeys: input.spec.metrics,
    });
  }

  return { ok: true, plan: emptyPlan };
}

function validateBudget(input: CompileMetricQuerySpecInput):
  | Readonly<{ ok: true; estimatedCells: number }>
  | MetricQueryCompileRefused {
  const estimatedCells = input.spec.metrics.length * estimateGrainMemberProduct(input);
  const maxCells = input.spec.budget?.maxCells;

  if (maxCells !== undefined && estimatedCells > maxCells) {
    return refused(
      'grain_budget_exceeded',
      'Requested metric grain exceeds the declared result budget.',
      { estimatedCells, maxCells },
      { grain: { level: [{ dimension: 'business_line', rollup: 'enterprise' }] } },
    );
  }

  return { ok: true, estimatedCells };
}

function buildFactWhere(
  input: CompileMetricQuerySpecInput,
  canonicalMetricKeys: readonly string[],
  dimensionFilters: readonly MetricQueryDimensionFilterPlan[],
  factScope: Extract<FactScopeValidation, { ok: true }>,
): MetricQueryFactWhere {
  const where: Record<string, unknown> = {
    tenantId: input.tenantId,
    metricKey: { in: [...canonicalMetricKeys] },
    periodType: input.spec.period.type,
    periodKey: { gte: input.spec.period.from, lte: input.spec.period.to },
    // #6544 — unconditional, never policy-gated: a retired fact must never
    // win a read just because its `asOf` is later than the genuine row's.
    // Retirement is a lifecycle fact, not an as-of-window concern.
    retiredAt: null,
  };

  if (typeof input.spec.asOfPolicy === 'object') {
    where.asOf = { lte: input.spec.asOfPolicy.asOf };
  }

  if (factScope.factScope === 'gl_dataset_run') {
    where.glDatasetId = factScope.snapshot.datasetId;
    where.glRunId = factScope.snapshot.runId;
  } else {
    where.glDatasetId = null;
    where.glRunId = null;
  }

  for (const filter of dimensionFilters) {
    where[filter.physicalColumn] = { in: [...filter.memberKeys] };
  }

  return where;
}

function estimateGrainMemberProduct(input: CompileMetricQuerySpecInput): number {
  return input.spec.grain.level.reduce((product, level) => {
    if (level.dimension === 'period') return product;
    // Enterprise rollup collapses the dimension to a single roll-up cell, so it never
    // multiplies by member fan-out (it is the fallback grain this compiler itself
    // recommends when a budget is exceeded).
    if (level.rollup === 'enterprise') return product;
    const filter = input.spec.filters.find((candidate) => candidate.dimension === level.dimension);
    if (filter && filter.memberKeys.length > 0) return product * filter.memberKeys.length;
    return product * Math.max(1, input.dimensionMembersByDimension[level.dimension]?.length ?? 1);
  }, 1);
}

function parsePeriodKey(type: string, key: string): number | null {
  if (type === 'fiscal_year' || type === 'year') {
    return /^\d{4}$/.test(key) ? Number(key) * 12 : null;
  }

  if (type === 'quarter') {
    const match = /^(\d{4})-Q([1-4])$/.exec(key);
    if (!match) return null;
    return Number(match[1]) * 4 + Number(match[2]);
  }

  if (type === 'month') {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(key);
    if (!match) return null;
    return Number(match[1]) * 12 + Number(match[2]);
  }

  return key.length > 0 ? key.localeCompare('') : null;
}

function entryAppliesToMembers(
  entry: MetricRegistryEntry,
  dimension: string,
  memberKeys: readonly string[],
): boolean {
  const scopes = entry.appliesTo.filter((scope) => scope.dimension === dimension);
  if (scopes.length === 0) return true;
  return scopes.some((scope) => scopeAppliesToMembers(scope, dimension, memberKeys));
}

function dimensionCatalogNeverBlendApplies(
  catalog: DimensionCatalogEntry | undefined,
  dimension: string,
  memberKeys: readonly string[],
): boolean {
  return (
    catalog?.neverBlend?.appliesTo.some((scope) =>
      scopeAppliesToMembers(scope, dimension, memberKeys),
    ) ?? false
  );
}

function scopeAppliesToMembers(
  scope: MetricRegistryAppliesTo,
  dimension: string,
  memberKeys: readonly string[],
): boolean {
  if (scope.dimension !== dimension) return false;
  if (scope.members === 'all') return true;
  if (memberKeys.length === 0) return true;
  return memberKeys.every((memberKey) => scope.members.includes(memberKey));
}

function refused(
  code: MetricQueryCompilerRefusalCode,
  message: string,
  details: Readonly<Record<string, unknown>>,
  counterProposal?: Partial<MetricQuerySpec>,
): MetricQueryCompileRefused {
  return { ok: false, refusal: { code, message, details, counterProposal } };
}

const emptyPlan: MetricQueryCompiledPlan = {
  tenantId: '',
  metricKeys: [],
  period: { type: '', from: '', to: '' },
  groupBy: [],
  factWhere: {},
  dimensionFilters: [],
  estimatedCells: 0,
  resolvedMetricAliases: {},
  metricResolutionProvenance: [],
};
